import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { useSelector } from 'react-redux';
import { selectMoodleUserState } from '../../../../support/slices/moodleSlice';
import {
  useDeletePostMutation,
  useUpdatePostMutation,
  useAddPostMutation,
  type MoodleInstance,
} from '../../../../hooks/useMoodleHooks';
import type { MoodleForumPost } from '../../../../types/moodleTypes';
import AddPostForm from './forms/AddPostForm';
import { plus, minus } from '@harbor/elements/icons';
import { HbrIcon } from '@harbor/elements-react-standalone';

type Props = {
  discussionId: number;
  post: MoodleForumPost;
  instance?: MoodleInstance;
};

export default function PostItem({ discussionId, post, instance = 'external' }: Props) {
  const moodleUser = useSelector(selectMoodleUserState);
  const currentUserId = moodleUser?.id;

  const del = useDeletePostMutation(discussionId, instance);
  const upd = useUpdatePostMutation(discussionId, instance);
  const add = useAddPostMutation(discussionId, instance);

  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(post.subject || (post as any)?.replysubject || '');
  const [message, setMessage] = useState(post.message || '');
  const [replyOpen, setReplyOpen] = useState(false);

  const authorId =
    (post as any)?.author?.id ??
    (post as any)?.userid ??
    (typeof (post as any)?.author === 'object' ? (post as any)?.author?.id : undefined);

  const isOwner =
    authorId != null && currentUserId != null && Number(authorId) === Number(currentUserId);

  const canReply = (post as any)?.capabilities?.reply !== false;

  const onSubmitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) return;
    upd.mutate(
      { postid: post.id, subject, message },
      {
        onSuccess: () => setEditing(false),
      }
    );
  };

  const onDelete = () => {
    if (!isOwner) return;
    del.mutate(post.id);
  };

  const onSubmitReply = (payload: { subject: string; message: string }) => {
    add.mutate(
      {
        postid: post.id,
        subject: payload.subject,
        message: payload.message,
      },
      {
        onSuccess: () => {
          setReplyOpen(false);
        },
      }
    );
  };

  return (
    <li className="post-item">
      <article className="post-card">
        <header className="post-header">
          <div className="post-author">
            {post.userfullname ||
              (typeof (post as any).author === 'string'
                ? (post as any).author
                : (post as any).author?.fullname) ||
              'User'}
          </div>
          <div className="post-actions">
            {canReply && (
              <button
                type="button"
                className="icon-btn"
                onClick={() => setReplyOpen((v) => !v)}
                aria-pressed={replyOpen}
                aria-label={replyOpen ? 'Close reply form' : 'Reply'}
                title={replyOpen ? 'Close reply form' : 'Reply'}
              >
                <HbrIcon asset={replyOpen ? minus : plus} />
              </button>
            )}
            {isOwner && !editing && (
              <>
                <button type="button" className="chip" onClick={() => setEditing(true)}>
                  Edit
                </button>
                <button
                  type="button"
                  className="chip danger"
                  onClick={onDelete}
                  disabled={del.isPending}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </header>

        {!editing ? (
          <div className="post-body">
            <div className="post-subject">
              {post.subject || (post as any)?.replysubject || 'No subject'}
            </div>
            <div
              className="post-message"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.message || '') }}
            />
          </div>
        ) : isOwner ? (
          <form className="editor" onSubmit={onSubmitEdit}>
            <div className="field">
              <label className="label" htmlFor={`ed-subject-${post.id}`}>Subject</label>
              <input
                id={`ed-subject-${post.id}`}
                className="input"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                disabled={upd.isPending}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor={`ed-message-${post.id}`}>Message</label>
              <textarea
                id={`ed-message-${post.id}`}
                className="textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                required
                disabled={upd.isPending}
              />
            </div>
            <div className="actions">
              <button type="submit" className="btn btn-primary" disabled={upd.isPending}>
                {upd.isPending ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {replyOpen && canReply && (
          <div className="reply-composer">
            <AddPostForm
              onSubmit={onSubmitReply}
              submitting={add.isPending}
              onBack={() => setReplyOpen(false)}
            />
          </div>
        )}
      </article>

      {post.children && post.children.length > 0 && (
        <ul className="nested-replies">
          {post.children.map((childPost) => (
            <PostItem
              key={childPost.id}
              discussionId={discussionId}
              post={childPost}
              instance={instance}
            />
          ))}
        </ul>
      )}

      <style>
        {`
          .post-card { border:1px solid rgba(0,0,0,0.12); border-radius:10px; background:#fff; padding: var(--hbr-spacing-md); }
          .post-header { display:flex; align-items:center; justify-content:space-between; margin-bottom: var(--hbr-spacing-xs); gap: var(--hbr-spacing-sm); }
          .post-author { font-family: var(--hbr-font-body); font-weight: var(--hbr-font-weight-semibold); }
          .post-actions { display:inline-flex; gap: var(--hbr-spacing-2xs); align-items: center; }
          .icon-btn {
            display:inline-flex; align-items:center; justify-content:center;
            width: 32px; height: 32px;
            border-radius: 8px; border: 1px solid rgba(0,0,0,0.15);
            background: #fff; cursor: pointer;
            transition: background 0.2s ease, box-shadow 0.2s ease;
          }
          .icon-btn:hover { background: rgba(10,102,255,0.06); box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
          .chip { display:inline-flex; align-items:center; padding:0.2rem 0.6rem; border-radius:999px; border:1px solid rgba(0,0,0,0.15); background:#fff; font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-xs); cursor:pointer; }
          .chip.danger { color:#b00020; border-color: rgba(176,0,32,0.35); }
          .post-subject { font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-md); font-weight: var(--hbr-font-weight-semibold); margin-bottom: var(--hbr-spacing-2xs); }
          .post-message { font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-md); }
          .editor { margin-top: var(--hbr-spacing-sm); }
          .field { margin-bottom: var(--hbr-spacing-sm); }
          .label { display:block; margin-bottom: var(--hbr-spacing-2xs); font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-sm); font-weight: var(--hbr-font-weight-semibold); }
          .input, .textarea { width:100%; padding:0.8rem 1rem; border-radius:10px; border:1px solid rgba(0,0,0,0.2); background:#fff; font-family:var(--hbr-font-body); font-size: var(--hbr-font-size-md); }
          .actions { margin-top: var(--hbr-spacing-sm); display:flex; gap: var(--hbr-spacing-sm); }
          .btn { display:inline-flex; align-items:center; justify-content:center; gap: var(--hbr-spacing-2xs); padding:0.6rem 1rem; border-radius:10px; border:1px solid rgba(0,0,0,0.2); background:#fff; color:#0a66ff; cursor:pointer; font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-sm); font-weight: var(--hbr-font-weight-semibold); }
          .btn-primary { background:#0a66ff; color:#fff; border-color: transparent; }
          .btn-primary:hover { background:#004fe0; }
          .btn-outline { background: transparent; color:#0a66ff; }
          .reply-composer { margin-top: var(--hbr-spacing-sm); }
          .nested-replies {
            list-style: none;
            padding-left: var(--hbr-spacing-lg, 32px);
            margin-top: var(--hbr-spacing-sm, 12px);
            border-left: 2px solid rgba(0,0,0,0.1);
          }
          .nested-replies > .post-item {
            padding-top: var(--hbr-spacing-sm, 12px);
          }
        `}
      </style>
    </li>
  );
}